import React from 'react';
import { useContext, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../AppContext/AuthContext';
import CorporateFacilityContext from '../AppContext/CorporateFacilityContext';
import { useCanAccessMediaLibraryAdmin } from '../Apps/MediaLibraryApp/components/useMediaLibraryProfile';
import { BsChevronDown } from 'react-icons/bs';


const AppHeader = () => {
  const navigate = useNavigate();
  const { handleLogOut, UserName } = useContext(AuthContext);
  const corporateCtx = useContext(CorporateFacilityContext);
  const Location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const { canAccess: canAccessMediaLibraryAdmin } = useCanAccessMediaLibraryAdmin();

  const menuItems = useMemo(() => {
    const items = [
      { label: 'Documents', to: '/newsletterapp' },
      { label: 'Calendar', to: '/calendarapp' },
      { label: 'Slides', to: '/slideapp' },
      { label: 'Designs', to: '/designs' },
      { label: 'Quick Print', to: '/quickprnts' },
    ];

    if (canAccessMediaLibraryAdmin) {
      items.push({ label: 'Media Library', to: '/medialibrary' });
    }

    return items;
  }, [canAccessMediaLibraryAdmin]);

  const userInitials = useMemo(() => {
    if (!UserName || typeof UserName !== 'string') return 'U';
    const normalized = UserName.trim();
    if (!normalized) return 'U';
    const words = normalized.split(/\s+/);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }, [UserName]);

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  const logOutHandler = () => {
    setShowDropdown(!showDropdown);
    handleLogOut();
  };

  const handleNavigation = () => {
    navigate('/slideapp');
  };

  const showCommunitySwitcher =
    corporateCtx?.isCorporateUser && corporateCtx?.activeFacility?.facilityName;

  const slideAppShell = Location.pathname === '/slideapp';
  const documentsShell =
    Location.pathname === '/newsletterapp' || Location.pathname === '/medialibrary';
  const shellBg = slideAppShell || documentsShell ? 'bg-[#f2efea]' : 'bg-[#faf7f2]';

  return (
    <div className={`w-full border-b border-[#ddd8d1] ${shellBg}`}>
      <div className="flex w-full items-center justify-between px-4 py-4 md:px-6">
        <div
          onClick={Location.pathname !== '/slideapp' ? () => handleNavigation() : undefined}
          className="cursor-pointer pr-4 md:pr-6"
        >
          <p className="font-specialElite text-[32px] leading-none text-[#2c2420]">prntyr</p>
          <p className="mt-1 text-[10px] italic text-[#b4b2a9]">(pronounced printer)</p>
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-between border-l border-[#ddd8d1] pl-4 md:pl-6">
          <div className="flex flex-1 items-center gap-8 text-[15px] text-[#7a6e6e]">
            {menuItems.map((item) => {
              const isActive = Location.pathname === item.to;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`border-b pb-1 transition-colors ${
                    isActive
                      ? 'border-[#9d2022] text-[#9d2022]'
                      : 'border-transparent hover:text-[#9d2022]'
                  }`}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </div>

          <div className="ml-4 flex shrink-0 items-center gap-4 md:gap-6">
            {showCommunitySwitcher ? (
              <div className="hidden items-center gap-2 text-[13px] text-[#7a6e6e] sm:flex">
                <span className="text-[#8f8577]">Community:</span>
                <span className="max-w-[160px] truncate font-medium text-[#3d3530]">
                  {corporateCtx.activeFacility.facilityName}
                </span>
                <button
                  type="button"
                  onClick={() => navigate('/corporate-communities')}
                  className="text-[#9d2022] underline-offset-2 hover:underline"
                >
                  Change
                </button>
              </div>
            ) : null}

            <div className="relative">
              <button
                onClick={handleDropdownToggle}
                className="inline-flex items-center gap-2.5 text-[15px] text-[#7a6e6e] hover:text-[#9d2022]"
              >
                <span className="inline-flex size-6 items-center justify-center rounded-full border border-[#ddd8d1] bg-[#f3f0eb] text-[10px] text-[#5c5252]">
                  {userInitials}
                </span>
                <span>{UserName}</span>
                <BsChevronDown className="text-[12px]" />
              </button>

              {showDropdown && (
                <div className="absolute right-0 z-[400] mt-2 w-44 rounded-md border border-[#e5dfd7] bg-white shadow-md">
                  <div className="py-1">
                    {showCommunitySwitcher ? (
                      <div className="border-b border-[#eee9e2] px-4 py-2 sm:hidden">
                        <p className="text-[11px] uppercase tracking-wide text-[#8f8577]">
                          Community
                        </p>
                        <p className="truncate text-sm font-medium text-[#3d3530]">
                          {corporateCtx.activeFacility.facilityName}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            handleDropdownToggle();
                            navigate('/corporate-communities');
                          }}
                          className="mt-1 text-sm text-[#9d2022] hover:underline"
                        >
                          Change community
                        </button>
                      </div>
                    ) : null}
                    <NavLink
                      onClick={handleDropdownToggle}
                      to="/profile"
                      className={`block px-4 py-2 text-sm ${
                        Location.pathname === '/profile' ? 'text-[#9d2022]' : 'text-[#5c5252]'
                      } hover:text-[#9d2022]`}
                    >
                      Profile
                    </NavLink>
                    <button
                      onClick={logOutHandler}
                      className="block w-full px-4 py-2 text-left text-sm text-[#5c5252] hover:text-[#9d2022]"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppHeader;
